import { useLayoutEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import TicketEntry from "./TicketEntry";

export const CONVERSATION_PAGE_SIZE = 20;
const TOP_THRESHOLD_PX = 48;

/**
 * The thread in a fixed-height pane that opens on the newest message. Scrolling near the top pulls
 * the next 20 older entries, and the view stays on the message the reader was looking at.
 */
const TicketConversation = ({ ticketId, api, entriesKey, viewerSide }) => {
  const paneRef = useRef(null);
  const anchorRef = useRef(null);
  const newestIdRef = useRef(null);

  const entries = useInfiniteQuery(
    entriesKey,
    ({ pageParam = 0 }) =>
      api.fetchEntries(ticketId, { page: pageParam, size: CONVERSATION_PAGE_SIZE }),
    {
      refetchOnMount: true,
      getNextPageParam: (last) =>
        last && last.currentPage + 1 < last.totalPages ? last.currentPage + 1 : undefined,
    }
  );

  // Pages are newest-first and a new reply shifts every page by one, so an entry can arrive twice.
  const thread = useMemo(() => {
    const seen = new Set();
    return (entries.data?.pages || [])
      .flatMap((page) => page?.content || [])
      .filter((entry) => !seen.has(entry.id) && seen.add(entry.id))
      .reverse();
  }, [entries.data]);

  const loadOlder = () => {
    const pane = paneRef.current;
    if (!pane || !entries.hasNextPage || entries.isFetchingNextPage) return;
    anchorRef.current = pane.scrollHeight - pane.scrollTop;
    entries.fetchNextPage();
  };

  useLayoutEffect(() => {
    const pane = paneRef.current;
    if (!pane || thread.length === 0) return;
    const newestId = thread[thread.length - 1].id;
    if (newestId !== newestIdRef.current) {
      newestIdRef.current = newestId;
      anchorRef.current = null;
      pane.scrollTop = pane.scrollHeight;
    } else if (anchorRef.current != null) {
      pane.scrollTop = pane.scrollHeight - anchorRef.current;
      anchorRef.current = null;
    }
    // A first page too short to scroll can never reach the top threshold, so keep filling.
    if (pane.clientHeight > 0 && pane.scrollHeight <= pane.clientHeight) loadOlder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread]);

  const onScroll = (event) => {
    if (event.currentTarget.scrollTop < TOP_THRESHOLD_PX) loadOlder();
  };

  if (entries.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <CircularProgress size={22} />
      </div>
    );
  }

  return (
    <div
      ref={paneRef}
      onScroll={onScroll}
      data-testid="ticket-conversation-pane"
      className="mt-3 max-h-[60vh] min-h-[12rem] overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white/70 px-3"
    >
      {entries.isFetchingNextPage ? (
        <div className="flex justify-center py-2">
          <CircularProgress size={18} />
        </div>
      ) : null}
      {!entries.hasNextPage && thread.length > 0 ? (
        <p className="m-0 py-2 text-center text-xs text-slate-400">Start of the conversation</p>
      ) : null}
      <ul className="m-0 list-none p-0" aria-label="Ticket conversation">
        {thread.map((entry) => (
          <TicketEntry key={entry.id} entry={entry} viewerSide={viewerSide} />
        ))}
      </ul>
    </div>
  );
};

export default TicketConversation;
